import { useMemo, type ReactNode } from 'react'
import { Combobox } from '@/components/Combobox'
import { FlagIcon } from '@/components/FlagIcon'
import { COUNTRY_LOCALE_MAP, COUNTRY_NAMES, getUserLocale } from '@/lib/locale'

/**
 * ISO-4217 code → display name. Built from the country map so we cover every
 * country we support locales for.
 */
const CURRENCY_NAME: Record<string, string> = {
  USD: 'US dollar',
  EUR: 'Euro',
  GBP: 'British pound',
  CAD: 'Canadian dollar',
  AUD: 'Australian dollar',
  NZD: 'New Zealand dollar',
  INR: 'Indian rupee',
  JPY: 'Japanese yen',
  CNY: 'Chinese yuan',
  KRW: 'South Korean won',
  SGD: 'Singapore dollar',
  HKD: 'Hong Kong dollar',
  MYR: 'Malaysian ringgit',
  AED: 'UAE dirham',
  SAR: 'Saudi riyal',
  EGP: 'Egyptian pound',
  NGN: 'Nigerian naira',
  KES: 'Kenyan shilling',
  BRL: 'Brazilian real',
  MXN: 'Mexican peso',
  ARS: 'Argentine peso',
  CLP: 'Chilean peso',
  COP: 'Colombian peso',
  ILS: 'Israeli shekel',
  TRY: 'Turkish lira',
  PLN: 'Polish zloty',
  CZK: 'Czech koruna',
  RON: 'Romanian leu',
  HUF: 'Hungarian forint',
  PHP: 'Philippine peso',
  THB: 'Thai baht',
  IDR: 'Indonesian rupiah',
  VND: 'Vietnamese dong',
  PKR: 'Pakistani rupee',
  BDT: 'Bangladeshi taka',
  LKR: 'Sri Lankan rupee',
  GHS: 'Ghanaian cedi',
  TZS: 'Tanzanian shilling',
  ZAR: 'South African rand',
  CHF: 'Swiss franc',
  SEK: 'Swedish krona',
  NOK: 'Norwegian krone',
  DKK: 'Danish krone',
}

/** Pick a representative country for each currency so the flag matches. */
const CURRENCY_FLAG_COUNTRY: Record<string, string> = {
  USD: 'US', EUR: 'EU', GBP: 'GB', CAD: 'CA', AUD: 'AU', NZD: 'NZ',
  INR: 'IN', JPY: 'JP', CNY: 'CN', KRW: 'KR', SGD: 'SG', HKD: 'HK', MYR: 'MY',
  AED: 'AE', SAR: 'SA', EGP: 'EG', NGN: 'NG', KES: 'KE',
  BRL: 'BR', MXN: 'MX', ARS: 'AR', CLP: 'CL', COP: 'CO',
  ILS: 'IL', TRY: 'TR', PLN: 'PL', CZK: 'CZ', RON: 'RO', HUF: 'HU',
  PHP: 'PH', THB: 'TH', IDR: 'ID', VND: 'VN', PKR: 'PK', BDT: 'BD', LKR: 'LK',
  GHS: 'GH', TZS: 'TZ', ZAR: 'ZA',
  CHF: 'CH', SEK: 'SE', NOK: 'NO', DKK: 'DK',
}

export type CurrencyComboboxProps = {
  value: string | null
  onChange: (v: string) => void
  placeholder?: string
}

export function CurrencyCombobox({ value, onChange, placeholder = 'Currency' }: CurrencyComboboxProps) {
  // Derive the list from the locale map (so changes to supported countries
  // flow through to the currency picker automatically).
  const options = useMemo(() => {
    const seen = new Set<string>()
    const entries: Array<{ value: string; label: string; description?: string; icon: ReactNode }> = []
    // Start with the "popular" currencies in a sensible order
    const priority = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR']
    for (const code of priority) {
      if (seen.has(code)) continue
      seen.add(code)
      const flagCode = CURRENCY_FLAG_COUNTRY[code] || ''
      const name = CURRENCY_NAME[code] || code
      entries.push({
        value: code,
        label: `${code} · ${name}`,
        description: flagCode,
        // EU has no ISO-3166 alpha-2 entry on flagcdn, render a fallback.
        icon: <FlagIcon code={flagCode === 'EU' ? null : flagCode} />,
      })
    }
    // Then everything else, alphabetical by name
    const all = Object.values(COUNTRY_LOCALE_MAP).map(e => e.currency)
    const unique = Array.from(new Set(all)).sort((a, b) => (CURRENCY_NAME[a] || a).localeCompare(CURRENCY_NAME[b] || b))
    for (const code of unique) {
      if (seen.has(code)) continue
      seen.add(code)
      const flagCode = CURRENCY_FLAG_COUNTRY[code] || ''
      const name = CURRENCY_NAME[code] || code
      entries.push({
        value: code,
        label: `${code} · ${name}`,
        description: flagCode,
        icon: <FlagIcon code={flagCode === 'EU' ? null : flagCode} />,
      })
    }
    return entries
  }, [])

  return (
    <Combobox<string>
      value={value}
      onChange={v => onChange(v ? String(v) : getUserLocale().currency)}
      options={options}
      placeholder={placeholder}
      emptyMessage="No currencies matched."
    />
  )
}

// Re-exports for consumers that still want the raw maps.
export { CURRENCY_NAME, CURRENCY_FLAG_COUNTRY, COUNTRY_LOCALE_MAP, COUNTRY_NAMES }
